import React, { useState, useRef } from 'react';
import { Box, Flex, Text, Input, Button, Grid, Badge, Table, Thead, Tbody, Tr, Th, Td, Select, useToast, Image, FormControl, FormLabel, Textarea, Checkbox, Stack, IconButton, Divider, Switch } from '@chakra-ui/react';
import { Lock, Users, Package, DollarSign, TrendingUp, Plus, Trash2, Edit2, LogOut, ShoppingBag, Tag, Settings } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { Product, PromoCode } from '../types';
import { categories } from '../data/mockData';
const PRESET_COLORS = [{
  name: 'Black',
  hex: '#000000'
}, {
  name: 'White',
  hex: '#FFFFFF'
}, {
  name: 'Red',
  hex: '#EF4444'
}, {
  name: 'Blue',
  hex: '#3B82F6'
}, {
  name: 'Navy',
  hex: '#1E3A8A'
}, {
  name: 'Green',
  hex: '#22C55E'
}, {
  name: 'Pink',
  hex: '#EC4899'
}, {
  name: 'Yellow',
  hex: '#EAB308'
}, {
  name: 'Orange',
  hex: '#F97316'
}, {
  name: 'Purple',
  hex: '#8B5CF6'
}, {
  name: 'Brown',
  hex: '#92400E'
}, {
  name: 'Grey',
  hex: '#6B7280'
}, {
  name: 'Beige',
  hex: '#D2B48C'
}, {
  name: 'Maroon',
  hex: '#7F1D1D'
}, {
  name: 'Cream',
  hex: '#FFFDD0'
}, {
  name: 'Sky Blue',
  hex: '#38BDF8'
}, {
  name: 'Teal',
  hex: '#14B8A6'
}, {
  name: 'Coral',
  hex: '#FF6B6B'
}, {
  name: 'Lavender',
  hex: '#C4B5FD'
}, {
  name: 'Olive',
  hex: '#65A30D'
}];
export const Admin: React.FC = () => {
  const {
    adminAuth,
    adminLogin,
    adminLogout,
    orders,
    products,
    updateOrderStatus,
    updateOrderDeliveryDate,
    addProduct,
    editProduct,
    deleteProduct,
    blockedUsers,
    blockUser,
    activateUser,
    promoCodes,
    addPromoCode,
    editPromoCode,
    deletePromoCode,
    adminSettings,
    updateAdminSettings
  } = useAppContext();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const toast = useToast();
  // Product Form State
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [productForm, setProductForm] = useState<Partial<Product>>({
    title: '',
    description: '',
    price: 0,
    discountPrice: 0,
    category: 'Men',
    stock: 0,
    images: [],
    sizes: [],
    colors: []
  });
  const [colorInput, setColorInput] = useState('');
  const [imageInput, setImageInput] = useState('');
  // Promo Form State
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [editingPromoId, setEditingPromoId] = useState<string | null>(null);
  const [promoForm, setPromoForm] = useState<Partial<PromoCode>>({
    code: '',
    discountType: 'percentage',
    discountValue: 0,
    productIds: [],
    expiryDate: '',
    usageLimit: 0,
    isActive: true
  });
  // Settings State
  const [settingsForm, setSettingsForm] = useState(adminSettings);
  const [newPassword, setNewPassword] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProductForm(prev => ({
          ...prev,
          images: [...(prev.images || []), reader.result as string]
        }));
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleLogin = () => {
    if (adminLogin(username, password)) toast({
      title: 'Welcome Admin',
      status: 'success'
    });else toast({
      title: 'Unauthorized Access',
      status: 'error'
    });
  };
  if (!adminAuth?.isAuthenticated) {
    return <Flex minH="100vh" align="center" justify="center" bg="gray.900" data-id="element-107">
        <Box bg="white" p={10} borderRadius="2xl" shadow="2xl" w="full" maxW="420px" textAlign="center" data-id="element-108">
          <Flex justify="center" mb={5} data-id="element-109">
            <Image src="https://cdn.magicpatterns.com/uploads/cNqMS3US7kkJPPcN3Awd9h/1000066978.png" h="50px" data-id="element-110" />
          </Flex>
          <Text fontSize="xl" fontWeight="800" mb={1} color="gray.800" data-id="element-111">
            Admin Panel
          </Text>
          <FormControl mb={4} data-id="element-112">
            <Input placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} size="lg" borderRadius="12px" bg="gray.50" border="none" data-id="element-113" />
          </FormControl>
          <FormControl mb={6} data-id="element-114">
            <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} size="lg" borderRadius="12px" bg="gray.50" border="none" onKeyPress={e => e.key === 'Enter' && handleLogin()} data-id="element-115" />
          </FormControl>
          <Button bg="gray.900" color="white" w="full" size="lg" borderRadius="12px" onClick={handleLogin} data-id="element-116">
            Login
          </Button>
        </Box>
      </Flex>;
  }
  // Analytics
  const totalRevenue = orders.filter(o => o.status === 'Delivered').reduce((sum, o) => sum + o.total, 0);
  const pendingOrders = orders.filter(o => o.status === 'Pending').length;
  const uniqueUsersMap = new Map();
  orders.forEach(order => {
    if (!uniqueUsersMap.has(order.customer.mobile)) uniqueUsersMap.set(order.customer.mobile, {
      ...order.customer,
      orderCount: 1
    });else uniqueUsersMap.get(order.customer.mobile).orderCount += 1;
  });
  const uniqueUsersList = Array.from(uniqueUsersMap.values());
  const totalPromoUsage = promoCodes.reduce((sum, p) => sum + p.usedCount, 0);
  const promoRevenue = orders.filter(o => o.promoCode && o.status === 'Delivered').reduce((sum, o) => sum + o.total, 0);
  // Handlers
  const handleProductSubmit = () => {
    if (!productForm.title || !productForm.price) return toast({
      title: 'Title and Price required',
      status: 'error'
    });
    const newProduct: Product = {
      id: editingProductId || `prod_${Date.now()}`,
      title: productForm.title || '',
      description: productForm.description || '',
      price: Number(productForm.price) || 0,
      discountPrice: Number(productForm.discountPrice) || 0,
      category: productForm.category || 'Men',
      stock: Number(productForm.stock) || 0,
      images: productForm.images?.filter(img => img.trim() !== '') || [],
      sizes: productForm.sizes || [],
      colors: productForm.colors || [],
      rating: 0,
      reviews: 0,
      isTrending: false
    };
    if (editingProductId) editProduct(editingProductId, newProduct);else addProduct(newProduct);
    setShowProductForm(false);
    setEditingProductId(null);
    setProductForm({
      title: '',
      description: '',
      price: 0,
      discountPrice: 0,
      category: 'Men',
      stock: 0,
      images: [],
      sizes: [],
      colors: []
    });
    toast({
      title: 'Product saved',
      status: 'success'
    });
  };
  const handlePromoSubmit = () => {
    if (!promoForm.code || !promoForm.discountValue || !promoForm.expiryDate) return toast({
      title: 'Code, Value, and Expiry required',
      status: 'error'
    });
    const newPromo: PromoCode = {
      id: editingPromoId || `promo_${Date.now()}`,
      code: promoForm.code.toUpperCase(),
      discountType: promoForm.discountType as 'percentage' | 'flat',
      discountValue: Number(promoForm.discountValue),
      productIds: promoForm.productIds || [],
      expiryDate: promoForm.expiryDate,
      usageLimit: Number(promoForm.usageLimit) || 0,
      usedCount: editingPromoId ? promoCodes.find(p => p.id === editingPromoId)?.usedCount || 0 : 0,
      isActive: promoForm.isActive ?? true,
      createdAt: new Date().toISOString()
    };
    if (editingPromoId) editPromoCode(editingPromoId, newPromo);else addPromoCode(newPromo);
    setShowPromoForm(false);
    setEditingPromoId(null);
    setPromoForm({
      code: '',
      discountType: 'percentage',
      discountValue: 0,
      productIds: [],
      expiryDate: '',
      usageLimit: 0,
      isActive: true
    });
    toast({
      title: 'Promo saved',
      status: 'success'
    });
  };
  const handleSettingsSave = () => {
    updateAdminSettings(settingsForm);
    if (newPassword) {
      // In a real app, update password logic here. For now, just clear it.
      setNewPassword('');
      toast({
        title: 'Password updated (simulated)',
        status: 'info'
      });
    }
    toast({
      title: 'Settings saved',
      status: 'success'
    });
  };
  const sidebarItems = [{
    key: 'dashboard',
    icon: <TrendingUp size={18} data-id="element-117" />,
    label: 'Dashboard'
  }, {
    key: 'products',
    icon: <ShoppingBag size={18} data-id="element-118" />,
    label: 'Products'
  }, {
    key: 'orders',
    icon: <Package size={18} data-id="element-119" />,
    label: 'Orders'
  }, {
    key: 'promos',
    icon: <Tag size={18} data-id="element-120" />,
    label: 'Promo Codes'
  }, {
    key: 'users',
    icon: <Users size={18} data-id="element-121" />,
    label: 'Users'
  }, {
    key: 'settings',
    icon: <Settings size={18} data-id="element-122" />,
    label: 'Settings'
  }];
  return <Box minH="100vh" bg="gray.50" fontFamily="'Poppins', sans-serif" data-id="element-123">
      <Flex bg="gray.900" color="white" px={6} py={3} align="center" justify="space-between" data-id="element-124">
        <Flex align="center" gap={3} data-id="element-125">
          <Image src="https://cdn.magicpatterns.com/uploads/cNqMS3US7kkJPPcN3Awd9h/1000066978.png" h="28px" data-id="element-126" />
          <Text fontSize="lg" fontWeight="700" data-id="element-127">
            Sky Brand Admin
          </Text>
        </Flex>
        <Button size="sm" variant="ghost" color="gray.300" leftIcon={<LogOut size={16} data-id="element-129" />} onClick={adminLogout} data-id="element-128">
          Logout
        </Button>
      </Flex>

      <Flex data-id="element-130">
        <Box w="220px" bg="white" minH="calc(100vh - 52px)" shadow="sm" py={6} px={3} data-id="element-131">
          <Stack spacing={1} data-id="element-132">
            {sidebarItems.map(item => <Button key={item.key} variant="ghost" justifyContent="flex-start" leftIcon={item.icon} onClick={() => setActiveTab(item.key)} bg={activeTab === item.key ? 'gray.900' : 'transparent'} color={activeTab === item.key ? 'white' : 'gray.600'} borderRadius="10px" fontSize="sm" data-id="element-133">
                {item.label}
              </Button>)}
          </Stack>
        </Box>

        <Box flex={1} p={6} data-id="element-134">
          {activeTab === 'dashboard' && <Box data-id="element-135">
              <Text fontSize="2xl" fontWeight="800" mb={6} data-id="element-136">
                Dashboard
              </Text>
              <Grid templateColumns="repeat(4, 1fr)" gap={5} mb={8} data-id="element-137">
                {[{
              label: 'Revenue',
              value: `₹${totalRevenue}`,
              color: 'blue',
              icon: <DollarSign size={20} data-id="element-138" />
            }, {
              label: 'Orders',
              value: orders.length,
              color: 'green',
              icon: <Package size={20} data-id="element-139" />
            }, {
              label: 'Pending',
              value: pendingOrders,
              color: 'orange',
              icon: <TrendingUp size={20} data-id="element-140" />
            }, {
              label: 'Customers',
              value: uniqueUsersList.length,
              color: 'purple',
              icon: <Users size={20} data-id="element-141" />
            }].map(stat => <Box key={stat.label} bg="white" p={5} borderRadius="16px" shadow="sm" data-id="element-142">
                    <Flex justify="space-between" align="center" data-id="element-143">
                      <Box data-id="element-144">
                        <Text color="gray.400" fontSize="xs" fontWeight="600" textTransform="uppercase" data-id="element-145">
                          {stat.label}
                        </Text>
                        <Text fontSize="2xl" fontWeight="800" mt={1} data-id="element-146">
                          {stat.value}
                        </Text>
                      </Box>
                      <Box bg={`${stat.color}.50`} p={3} borderRadius="12px" color={`${stat.color}.500`} data-id="element-147">
                        {stat.icon}
                      </Box>
                    </Flex>
                  </Box>)}
              </Grid>

              <Text fontSize="xl" fontWeight="700" mb={4} data-id="element-148">
                Promo Analytics
              </Text>
              <Grid templateColumns="repeat(4, 1fr)" gap={5} mb={8} data-id="element-149">
                <Box bg="white" p={5} borderRadius="16px" shadow="sm" data-id="element-150">
                  <Text color="gray.400" fontSize="xs" fontWeight="600" data-id="element-151">
                    TOTAL PROMOS
                  </Text>
                  <Text fontSize="xl" fontWeight="800" data-id="element-152">
                    {promoCodes.length}
                  </Text>
                </Box>
                <Box bg="white" p={5} borderRadius="16px" shadow="sm" data-id="element-153">
                  <Text color="gray.400" fontSize="xs" fontWeight="600" data-id="element-154">
                    ACTIVE PROMOS
                  </Text>
                  <Text fontSize="xl" fontWeight="800" data-id="element-155">
                    {promoCodes.filter(p => p.isActive && new Date(p.expiryDate) > new Date()).length}
                  </Text>
                </Box>
                <Box bg="white" p={5} borderRadius="16px" shadow="sm" data-id="element-156">
                  <Text color="gray.400" fontSize="xs" fontWeight="600" data-id="element-157">
                    TOTAL USAGE
                  </Text>
                  <Text fontSize="xl" fontWeight="800" data-id="element-158">
                    {totalPromoUsage}
                  </Text>
                </Box>
                <Box bg="white" p={5} borderRadius="16px" shadow="sm" data-id="element-159">
                  <Text color="gray.400" fontSize="xs" fontWeight="600" data-id="element-160">
                    PROMO REVENUE
                  </Text>
                  <Text fontSize="xl" fontWeight="800" data-id="element-161">
                    ₹{promoRevenue}
                  </Text>
                </Box>
              </Grid>
            </Box>}

          {activeTab === 'products' && <Box data-id="element-162">
              <Flex justify="space-between" align="center" mb={6} data-id="element-163">
                <Text fontSize="2xl" fontWeight="800" data-id="element-164">
                  Products
                </Text>
                <Button bg="gray.900" color="white" leftIcon={<Plus size={16} data-id="element-166" />} onClick={() => {
              setProductForm({
                title: '',
                description: '',
                price: 0,
                discountPrice: 0,
                category: 'Men',
                stock: 0,
                images: [],
                sizes: [],
                colors: []
              });
              setEditingProductId(null);
              setShowProductForm(!showProductForm);
            }} data-id="element-165">
                  {showProductForm ? 'Cancel' : 'Add Product'}
                </Button>
              </Flex>
              {showProductForm && <Box bg="white" p={6} borderRadius="16px" shadow="sm" mb={6} data-id="element-167">
                  <Stack spacing={6} data-id="element-168">
                    {/* Basic Info */}
                    <Box data-id="element-169">
                      <Text fontSize="md" fontWeight="700" mb={4} color="gray.800" data-id="element-170">
                        Basic Information
                      </Text>
                      <Grid templateColumns="repeat(2, 1fr)" gap={4} data-id="element-171">
                        <FormControl isRequired data-id="element-172">
                          <FormLabel fontSize="xs" data-id="element-173">Title</FormLabel>
                          <Input value={productForm.title} onChange={e => setProductForm({
                      ...productForm,
                      title: e.target.value
                    })} data-id="element-174" />
                        </FormControl>
                        <FormControl isRequired data-id="element-175">
                          <FormLabel fontSize="xs" data-id="element-176">Category</FormLabel>
                          <Select value={productForm.category} onChange={e => setProductForm({
                      ...productForm,
                      category: e.target.value
                    })} data-id="element-177">
                            {categories.filter(c => c !== 'All').map(c => <option key={c} value={c} data-id="element-178">
                                  {c}
                                </option>)}
                          </Select>
                        </FormControl>
                        <FormControl isRequired data-id="element-179">
                          <FormLabel fontSize="xs" data-id="element-180">Price</FormLabel>
                          <Input type="number" value={productForm.price} onChange={e => setProductForm({
                      ...productForm,
                      price: Number(e.target.value)
                    })} data-id="element-181" />
                        </FormControl>
                        <FormControl data-id="element-182">
                          <FormLabel fontSize="xs" data-id="element-183">Discount Price</FormLabel>
                          <Input type="number" value={productForm.discountPrice} onChange={e => setProductForm({
                      ...productForm,
                      discountPrice: Number(e.target.value)
                    })} data-id="element-184" />
                        </FormControl>
                        <FormControl data-id="element-185">
                          <FormLabel fontSize="xs" data-id="element-186">Stock</FormLabel>
                          <Input type="number" value={productForm.stock} onChange={e => setProductForm({
                      ...productForm,
                      stock: Number(e.target.value)
                    })} data-id="element-187" />
                        </FormControl>
                        <FormControl gridColumn="span 2" data-id="element-188">
                          <FormLabel fontSize="xs" data-id="element-189">Description</FormLabel>
                          <Textarea value={productForm.description} onChange={e => setProductForm({
                      ...productForm,
                      description: e.target.value
                    })} data-id="element-190" />
                        </FormControl>
                        <FormControl gridColumn="span 2" data-id="element-191">
                          <FormLabel fontSize="xs" data-id="element-192">Sizes</FormLabel>
                          <Stack direction="row" data-id="element-193">
                            {['S', 'M', 'L', 'XL', 'XXL'].map(s => <Checkbox key={s} isChecked={productForm.sizes?.includes(s)} onChange={() => setProductForm(prev => ({
                        ...prev,
                        sizes: prev.sizes?.includes(s) ? prev.sizes.filter(x => x !== s) : [...(prev.sizes || []), s]
                      }))} data-id="element-194">
                                {s}
                              </Checkbox>)}
                          </Stack>
                        </FormControl>
                      </Grid>
                    </Box>

                    <Divider data-id="element-195" />

                    {/* Image Management */}
                    <Box data-id="element-196">
                      <Flex justify="space-between" align="center" mb={4} data-id="element-197">
                        <Text fontSize="md" fontWeight="700" color="gray.800" data-id="element-198">
                          Product Images
                        </Text>
                        <Text fontSize="xs" color="gray.500" data-id="element-199">
                          {productForm.images?.length || 0} images added
                        </Text>
                      </Flex>

                      <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4} data-id="element-200">
                        <Box data-id="element-201">
                          <input type="file" multiple accept="image/*" ref={fileInputRef} style={{
                      display: 'none'
                    }} onChange={handleImageUpload} data-id="element-202" />
                          <Button w="full" variant="outline" onClick={() => fileInputRef.current?.click()} data-id="element-203">
                            Upload from Device
                          </Button>
                        </Box>
                        <Flex gap={2} data-id="element-204">
                          <Input placeholder="Image URL" value={imageInput} onChange={e => setImageInput(e.target.value)} data-id="element-205" />
                          <Button onClick={() => {
                      if (imageInput.trim()) {
                        setProductForm(prev => ({
                          ...prev,
                          images: [...(prev.images || []), imageInput.trim()]
                        }));
                        setImageInput('');
                      }
                    }} data-id="element-206">
                            Add
                          </Button>
                        </Flex>
                      </Grid>

                      {productForm.images && productForm.images.length > 0 && <Flex wrap="wrap" gap={3} data-id="element-207">
                          {productForm.images.map((img, idx) => <Box key={idx} position="relative" w="80px" h="80px" borderRadius="8px" overflow="hidden" borderWidth="1px" borderColor="gray.200" data-id="element-208">
                              <Image src={img} w="full" h="full" objectFit="cover" data-id="element-209" />
                              <IconButton aria-label="Remove image" icon={<Trash2 size={12} data-id="element-211" />} size="xs" colorScheme="red" position="absolute" top={1} right={1} borderRadius="full" onClick={() => {
                      setProductForm(prev => ({
                        ...prev,
                        images: prev.images?.filter((_, i) => i !== idx)
                      }));
                    }} data-id="element-210" />
                            </Box>)}
                        </Flex>}
                    </Box>

                    <Divider data-id="element-212" />

                    {/* Color Management */}
                    <Box data-id="element-213">
                      <Text fontSize="md" fontWeight="700" mb={4} color="gray.800" data-id="element-214">
                        Available Colors
                      </Text>

                      <Grid templateColumns="repeat(10, 1fr)" gap={2} mb={4} data-id="element-215">
                        {PRESET_COLORS.map(color => {
                    const isSelected = productForm.colors?.includes(color.name);
                    return <Box key={color.name} w="40px" h="40px" borderRadius="full" bg={color.hex} cursor="pointer" borderWidth="2px" borderColor={isSelected ? 'gray.900' : 'gray.200'} position="relative" onClick={() => {
                      setProductForm(prev => ({
                        ...prev,
                        colors: isSelected ? prev.colors?.filter(c => c !== color.name) : [...(prev.colors || []), color.name]
                      }));
                    }} title={color.name} data-id="element-216">
                              {isSelected && <Flex position="absolute" inset={0} align="center" justify="center" bg="blackAlpha.400" borderRadius="full" data-id="element-217">
                                  <Text color="white" fontSize="xs" data-id="element-218">
                                    ✓
                                  </Text>
                                </Flex>}
                            </Box>;
                  })}
                      </Grid>

                      <Flex gap={2} align="center" mb={4} data-id="element-219">
                        <Input type="color" w="60px" p={1} h="40px" value={colorInput.startsWith('#') ? colorInput : '#000000'} onChange={e => setColorInput(e.target.value)} data-id="element-220" />
                        <Input placeholder="Custom Color Name" value={colorInput.startsWith('#') ? '' : colorInput} onChange={e => setColorInput(e.target.value)} data-id="element-221" />
                        <Button onClick={() => {
                    if (colorInput.trim()) {
                      setProductForm(prev => ({
                        ...prev,
                        colors: [...(prev.colors || []), colorInput.trim()]
                      }));
                      setColorInput('');
                    }
                  }} data-id="element-222">
                          Add
                        </Button>
                      </Flex>

                      {productForm.colors && productForm.colors.length > 0 && <Flex wrap="wrap" gap={2} data-id="element-223">
                          {productForm.colors.map((color, idx) => <Badge key={idx} px={3} py={1} borderRadius="full" textTransform="none" display="flex" alignItems="center" gap={2} data-id="element-224">
                              {color}
                              <Box as="span" cursor="pointer" onClick={() => {
                      setProductForm(prev => ({
                        ...prev,
                        colors: prev.colors?.filter(c => c !== color)
                      }));
                    }} data-id="element-225">
                                ×
                              </Box>
                            </Badge>)}
                        </Flex>}
                    </Box>
                  </Stack>

                  <Button mt={6} w="full" size="lg" bg="gray.900" color="white" onClick={handleProductSubmit} data-id="element-226">
                    Save Product
                  </Button>
                </Box>}
              <Box bg="white" p={6} borderRadius="16px" shadow="sm" data-id="element-227">
                <Table variant="simple" size="sm" data-id="element-228">
                  <Thead data-id="element-229">
                    <Tr data-id="element-230">
                      <Th data-id="element-231">Name</Th>
                      <Th data-id="element-232">Price</Th>
                      <Th data-id="element-233">Stock</Th>
                      <Th data-id="element-234">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody data-id="element-235">
                    {products.map(p => <Tr key={p.id} data-id="element-236">
                        <Td data-id="element-237">{p.title}</Td>
                        <Td data-id="element-238">₹{p.discountPrice}</Td>
                        <Td data-id="element-239">{p.stock}</Td>
                        <Td data-id="element-240">
                          <IconButton aria-label="edit" icon={<Edit2 size={14} data-id="element-242" />} size="xs" onClick={() => {
                      setProductForm(p);
                      setEditingProductId(p.id);
                      setShowProductForm(true);
                    }} mr={2} data-id="element-241" />
                          <IconButton aria-label="delete" icon={<Trash2 size={14} data-id="element-244" />} size="xs" colorScheme="red" onClick={() => deleteProduct(p.id)} data-id="element-243" />
                        </Td>
                      </Tr>)}
                  </Tbody>
                </Table>
              </Box>
            </Box>}

          {activeTab === 'promos' && <Box data-id="element-245">
              <Flex justify="space-between" align="center" mb={6} data-id="element-246">
                <Text fontSize="2xl" fontWeight="800" data-id="element-247">
                  Promo Codes
                </Text>
                <Button bg="gray.900" color="white" leftIcon={<Plus size={16} data-id="element-249" />} onClick={() => {
              setPromoForm({
                code: '',
                discountType: 'percentage',
                discountValue: 0,
                productIds: [],
                expiryDate: '',
                usageLimit: 0,
                isActive: true
              });
              setEditingPromoId(null);
              setShowPromoForm(!showPromoForm);
            }} data-id="element-248">
                  {showPromoForm ? 'Cancel' : 'Create Promo'}
                </Button>
              </Flex>
              {showPromoForm && <Box bg="white" p={6} borderRadius="16px" shadow="sm" mb={6} data-id="element-250">
                  <Grid templateColumns="repeat(2, 1fr)" gap={4} data-id="element-251">
                    <FormControl isRequired data-id="element-252">
                      <FormLabel fontSize="xs" data-id="element-253">Code</FormLabel>
                      <Input value={promoForm.code} onChange={e => setPromoForm({
                  ...promoForm,
                  code: e.target.value.toUpperCase()
                })} textTransform="uppercase" data-id="element-254" />
                    </FormControl>
                    <FormControl isRequired data-id="element-255">
                      <FormLabel fontSize="xs" data-id="element-256">Type</FormLabel>
                      <Select value={promoForm.discountType} onChange={e => setPromoForm({
                  ...promoForm,
                  discountType: e.target.value as any
                })} data-id="element-257">
                        <option value="percentage" data-id="element-258">Percentage (%)</option>
                        <option value="flat" data-id="element-259">Flat Amount (₹)</option>
                      </Select>
                    </FormControl>
                    <FormControl isRequired data-id="element-260">
                      <FormLabel fontSize="xs" data-id="element-261">Value</FormLabel>
                      <Input type="number" value={promoForm.discountValue} onChange={e => setPromoForm({
                  ...promoForm,
                  discountValue: Number(e.target.value)
                })} data-id="element-262" />
                    </FormControl>
                    <FormControl isRequired data-id="element-263">
                      <FormLabel fontSize="xs" data-id="element-264">Expiry Date</FormLabel>
                      <Input type="date" value={promoForm.expiryDate} onChange={e => setPromoForm({
                  ...promoForm,
                  expiryDate: e.target.value
                })} data-id="element-265" />
                    </FormControl>
                    <FormControl data-id="element-266">
                      <FormLabel fontSize="xs" data-id="element-267">
                        Usage Limit (0 = unlimited)
                      </FormLabel>
                      <Input type="number" value={promoForm.usageLimit} onChange={e => setPromoForm({
                  ...promoForm,
                  usageLimit: Number(e.target.value)
                })} data-id="element-268" />
                    </FormControl>
                    <FormControl display="flex" alignItems="center" mt={6} data-id="element-269">
                      <FormLabel mb="0" data-id="element-270">Active</FormLabel>
                      <Switch isChecked={promoForm.isActive} onChange={e => setPromoForm({
                  ...promoForm,
                  isActive: e.target.checked
                })} data-id="element-271" />
                    </FormControl>
                  </Grid>
                  <Button mt={4} bg="gray.900" color="white" onClick={handlePromoSubmit} data-id="element-272">
                    Save Promo
                  </Button>
                </Box>}
              <Box bg="white" p={6} borderRadius="16px" shadow="sm" data-id="element-273">
                <Table variant="simple" size="sm" data-id="element-274">
                  <Thead data-id="element-275">
                    <Tr data-id="element-276">
                      <Th data-id="element-277">Code</Th>
                      <Th data-id="element-278">Discount</Th>
                      <Th data-id="element-279">Expiry</Th>
                      <Th data-id="element-280">Usage</Th>
                      <Th data-id="element-281">Status</Th>
                      <Th data-id="element-282">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody data-id="element-283">
                    {promoCodes.map(p => {
                  const isExpired = new Date(p.expiryDate) < new Date();
                  return <Tr key={p.id} data-id="element-284">
                          <Td fontWeight="bold" data-id="element-285">{p.code}</Td>
                          <Td data-id="element-286">
                            {p.discountType === 'percentage' ? `${p.discountValue}%` : `₹${p.discountValue}`}
                          </Td>
                          <Td data-id="element-287">{p.expiryDate}</Td>
                          <Td data-id="element-288">
                            {p.usedCount} / {p.usageLimit || '∞'}
                          </Td>
                          <Td data-id="element-289">
                            <Badge colorScheme={!p.isActive ? 'gray' : isExpired ? 'red' : 'green'} data-id="element-290">
                              {!p.isActive ? 'Disabled' : isExpired ? 'Expired' : 'Active'}
                            </Badge>
                          </Td>
                          <Td data-id="element-291">
                            <IconButton aria-label="edit" icon={<Edit2 size={14} data-id="element-293" />} size="xs" onClick={() => {
                        setPromoForm(p);
                        setEditingPromoId(p.id);
                        setShowPromoForm(true);
                      }} mr={2} data-id="element-292" />
                            <IconButton aria-label="delete" icon={<Trash2 size={14} data-id="element-295" />} size="xs" colorScheme="red" onClick={() => deletePromoCode(p.id)} data-id="element-294" />
                          </Td>
                        </Tr>;
                })}
                  </Tbody>
                </Table>
              </Box>
            </Box>}

          {activeTab === 'orders' && <Box bg="white" p={6} borderRadius="16px" shadow="sm" data-id="element-296">
              <Text fontSize="2xl" fontWeight="800" mb={6} data-id="element-297">
                Orders
              </Text>
              <Box overflowX="auto" data-id="element-298">
                <Table variant="simple" size="sm" data-id="element-299">
                  <Thead data-id="element-300">
                    <Tr data-id="element-301">
                      <Th data-id="element-302">ID</Th>
                      <Th data-id="element-303">Customer</Th>
                      <Th data-id="element-304">Total</Th>
                      <Th data-id="element-305">Status</Th>
                      <Th data-id="element-306">Expected Delivery</Th>
                      <Th data-id="element-307">Delivered On</Th>
                    </Tr>
                  </Thead>
                  <Tbody data-id="element-308">
                    {orders.map(o => <Tr key={o.id} data-id="element-309">
                        <Td data-id="element-310">{o.id}</Td>
                        <Td data-id="element-311">{o.customer.name}</Td>
                        <Td data-id="element-312">
                          ₹{o.total}{' '}
                          {o.promoCode && <Badge ml={1} colorScheme="green" fontSize="8px" data-id="element-313">
                              {o.promoCode}
                            </Badge>}
                        </Td>
                        <Td data-id="element-314">
                          <Select size="xs" value={o.status} onChange={e => updateOrderStatus(o.id, e.target.value as any)} data-id="element-315">
                            <option value="Pending" data-id="element-316">Pending</option>
                            <option value="Confirmed" data-id="element-317">Confirmed</option>
                            <option value="Packed" data-id="element-318">Packed</option>
                            <option value="Out for Delivery" data-id="element-319">
                              Out for Delivery
                            </option>
                            <option value="Delivered" data-id="element-320">Delivered</option>
                            <option value="Cancelled" data-id="element-321">Cancelled</option>
                          </Select>
                        </Td>
                        <Td data-id="element-322">
                          <Input type="date" size="xs" value={o.expectedDeliveryDate || ''} onChange={e => updateOrderDeliveryDate(o.id, e.target.value, o.deliveryDate)} data-id="element-323" />
                        </Td>
                        <Td data-id="element-324">
                          <Input type="date" size="xs" value={o.deliveryDate || ''} onChange={e => updateOrderDeliveryDate(o.id, o.expectedDeliveryDate || '', e.target.value)} isDisabled={o.status !== 'Delivered'} data-id="element-325" />
                        </Td>
                      </Tr>)}
                  </Tbody>
                </Table>
              </Box>
            </Box>}

          {activeTab === 'settings' && <Box bg="white" p={6} borderRadius="16px" shadow="sm" maxW="600px" data-id="element-326">
              <Text fontSize="2xl" fontWeight="800" mb={6} data-id="element-327">
                Advanced Settings
              </Text>
              <Grid templateColumns="repeat(2, 1fr)" gap={6} data-id="element-328">
                <FormControl data-id="element-329">
                  <FormLabel fontSize="sm" data-id="element-330">Accent Color</FormLabel>
                  <Input type="color" value={settingsForm.accentColor} onChange={e => setSettingsForm({
                ...settingsForm,
                accentColor: e.target.value
              })} h="40px" p={1} data-id="element-331" />
                </FormControl>
                <FormControl data-id="element-332">
                  <FormLabel fontSize="sm" data-id="element-333">Delivery Charge (₹)</FormLabel>
                  <Input type="number" value={settingsForm.deliveryCharge} onChange={e => setSettingsForm({
                ...settingsForm,
                deliveryCharge: Number(e.target.value)
              })} data-id="element-334" />
                </FormControl>
                <FormControl gridColumn="span 2" data-id="element-335">
                  <FormLabel fontSize="sm" data-id="element-336">Banner Heading</FormLabel>
                  <Input value={settingsForm.bannerText} onChange={e => setSettingsForm({
                ...settingsForm,
                bannerText: e.target.value
              })} data-id="element-337" />
                </FormControl>
                <FormControl gridColumn="span 2" data-id="element-338">
                  <FormLabel fontSize="sm" data-id="element-339">Banner Subtext</FormLabel>
                  <Input value={settingsForm.bannerSubtext} onChange={e => setSettingsForm({
                ...settingsForm,
                bannerSubtext: e.target.value
              })} data-id="element-340" />
                </FormControl>

                <FormControl display="flex" alignItems="center" data-id="element-341">
                  <FormLabel mb="0" fontSize="sm" data-id="element-342">
                    Enable Animations
                  </FormLabel>
                  <Switch isChecked={settingsForm.animationsEnabled} onChange={e => setSettingsForm({
                ...settingsForm,
                animationsEnabled: e.target.checked
              })} data-id="element-343" />
                </FormControl>
                <FormControl display="flex" alignItems="center" data-id="element-344">
                  <FormLabel mb="0" fontSize="sm" data-id="element-345">
                    Enable Promo System
                  </FormLabel>
                  <Switch isChecked={settingsForm.promoSystemEnabled} onChange={e => setSettingsForm({
                ...settingsForm,
                promoSystemEnabled: e.target.checked
              })} data-id="element-346" />
                </FormControl>
                <FormControl display="flex" alignItems="center" data-id="element-347">
                  <FormLabel mb="0" fontSize="sm" data-id="element-348">
                    WhatsApp Notifications
                  </FormLabel>
                  <Switch isChecked={settingsForm.whatsappNotifications} onChange={e => setSettingsForm({
                ...settingsForm,
                whatsappNotifications: e.target.checked
              })} data-id="element-349" />
                </FormControl>
                <FormControl display="flex" alignItems="center" data-id="element-350">
                  <FormLabel mb="0" fontSize="sm" color="red.500" data-id="element-351">
                    Maintenance Mode
                  </FormLabel>
                  <Switch colorScheme="red" isChecked={settingsForm.maintenanceMode} onChange={e => setSettingsForm({
                ...settingsForm,
                maintenanceMode: e.target.checked
              })} data-id="element-352" />
                </FormControl>
              </Grid>
              <Divider my={6} data-id="element-353" />
              <Text fontSize="lg" fontWeight="700" mb={4} data-id="element-354">
                Change Password
              </Text>
              <FormControl mb={4} data-id="element-355">
                <FormLabel fontSize="sm" data-id="element-356">New Password</FormLabel>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} data-id="element-357" />
              </FormControl>
              <Button mt={4} bg="gray.900" color="white" onClick={handleSettingsSave} data-id="element-358">
                Save All Settings
              </Button>
            </Box>}

          {activeTab === 'users' && <Box bg="white" p={6} borderRadius="16px" shadow="sm" data-id="element-359">
              <Text fontSize="2xl" fontWeight="800" mb={6} data-id="element-360">
                Users
              </Text>
              <Table variant="simple" size="sm" data-id="element-361">
                <Thead data-id="element-362">
                  <Tr data-id="element-363">
                    <Th data-id="element-364">Name</Th>
                    <Th data-id="element-365">Mobile</Th>
                    <Th data-id="element-366">Orders</Th>
                    <Th data-id="element-367">Status</Th>
                    <Th data-id="element-368">Action</Th>
                  </Tr>
                </Thead>
                <Tbody data-id="element-369">
                  {uniqueUsersList.map((u, idx) => {
                const isBlocked = blockedUsers.includes(u.mobile);
                return <Tr key={idx} data-id="element-370">
                        <Td data-id="element-371">{u.name}</Td>
                        <Td data-id="element-372">{u.mobile}</Td>
                        <Td data-id="element-373">{u.orderCount}</Td>
                        <Td data-id="element-374">
                          <Badge colorScheme={isBlocked ? 'red' : 'green'} data-id="element-375">
                            {isBlocked ? 'Blocked' : 'Active'}
                          </Badge>
                        </Td>
                        <Td data-id="element-376">
                          <Button size="xs" colorScheme={isBlocked ? 'green' : 'red'} onClick={() => isBlocked ? activateUser(u.mobile) : blockUser(u.mobile)} data-id="element-377">
                            {isBlocked ? 'Activate' : 'Block'}
                          </Button>
                        </Td>
                      </Tr>;
              })}
                </Tbody>
              </Table>
            </Box>}
        </Box>
      </Flex>
    </Box>;
};